<template>
  <div class="bc-sheet-ov">
    <div ref="sheet"
         class="bc-sheet"
         role="dialog"
         aria-modal="true"
         :aria-labelledby="titleId"
         tabindex="-1">
      <div class="bc-sheet-head">
        <button v-if="showBack"
                type="button"
                class="btn btn-default bc-ic"
                :aria-label="$t('broadcast.back')"
                :disabled="locked"
                @click="$emit('back')">
          <i class="fal fa-arrow-left"></i>
        </button>
        <span v-else></span>
        <span class="bc-hr">
          <span v-if="total"
                class="bc-n">{{ step }}/{{ total }}</span>
          <button type="button"
                  class="btn btn-default bc-ic"
                  :aria-label="$t('broadcast.close')"
                  :disabled="locked"
                  @click="$emit('close')">
            <i class="fal fa-times"></i>
          </button>
        </span>
      </div>
      <div class="bc-sheet-main">
        <h4 v-if="title"
            :id="titleId"
            class="bc-q">{{ title }}</h4>
        <p v-if="why"
           class="bc-why">{{ why }}</p>
        <slot></slot>
      </div>
      <div v-if="$slots.foot"
           class="bc-sheet-foot">
        <slot name="foot"></slot>
      </div>
    </div>
  </div>
</template>

<script>
let uid = 0;

// The บรอดแคสต์ popup shell: bottom sheet up to 720 px wide, centred modal above. It owns one
// browser-history entry while open, so the phone's back button steps back (showBack) or closes
// instead of leaving the page. The parent renders it with v-if and handles back/close.
// - Esc and the scrim emit close (back when dismissBack is set); both ignored while locked.
// - phone/browser back: showBack ? emit back (the entry is re-pushed) : emit close.
export default {
  name: "BcSheet",
  props: {
    step: { type: Number, default: 1 },
    total: { type: Number, default: 0 }, // 0 hides the n/N counter
    showBack: { type: Boolean, default: false },
    locked: { type: Boolean, default: false }, // a send is in flight: nothing dismisses the sheet
    dismissBack: { type: Boolean, default: false }, // Esc and scrim step back instead of closing
    title: { type: String, default: "" },
    why: { type: String, default: "" },
  },

  data() {
    return { pushed: false, hash: "", titleId: "bc-sheet-title-" + ++uid };
  },

  mounted() {
    document.body.classList.add("--bc-sheet-open");
    document.addEventListener("keydown", this.onKey);
    window.addEventListener("popstate", this.onPop);
    try {
      history.pushState({ bcSheet: 1 }, "", location.href);
      this.pushed = true;
      this.hash = location.hash;
    } catch (e) {
      this.pushed = false;
    }
    this.$nextTick(() => {
      if (this.$refs.sheet) this.$refs.sheet.focus({ preventScroll: true });
    });
  },

  beforeDestroy() {
    document.body.classList.remove("--bc-sheet-open");
    document.removeEventListener("keydown", this.onKey);
    window.removeEventListener("popstate", this.onPop);
    // Closed by the parent: give the history entry back (unless the route already moved on)
    if (this.pushed && location.hash === this.hash && history.state && history.state.bcSheet) {
      this.pushed = false;
      history.back();
    }
  },

  methods: {
    dismiss() {
      if (this.locked) return;
      this.$emit(this.dismissBack && this.showBack ? "back" : "close");
    },

    onKey(event) {
      if (event.key === "Escape" || event.keyCode === 27) this.dismiss();
    },

    onPop() {
      if (!this.pushed) return;
      if (location.hash !== this.hash) {
        // the route is moving: the entry is no longer ours to pop
        this.pushed = false;
        return;
      }
      if (this.locked) {
        history.pushState({ bcSheet: 1 }, "", location.href);
      } else if (this.showBack) {
        history.pushState({ bcSheet: 1 }, "", location.href);
        this.$emit("back");
      } else {
        this.pushed = false; // the browser already popped it
        this.$emit("close");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.bc-sheet-ov {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1050;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

@media (min-width: 721px) {
  .bc-sheet-ov {
    align-items: center;
    padding: 2rem;
  }
}

.bc-sheet {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 720px;
  height: auto;
  max-height: 88vh;
  max-height: 88dvh;
  overflow: hidden;
  outline: none;
  background: #fff;
  border-radius: 16px 16px 0 0;
}

@media (min-width: 721px) {
  .bc-sheet {
    border-radius: 16px;
  }
}

.bc-sheet-head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background: #fff;
}

.bc-hr {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.bc-ic {
  min-width: 44px;
  min-height: 44px;
  padding: 0 0.75rem;
}

.bc-n {
  font-weight: 700;
  font-size: 1rem;
  color: #646464;
}

.bc-sheet-main {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 1.25rem 1.25rem;
}

.bc-q {
  font-size: 1.4rem;
  font-weight: 300;
  color: #333;
  margin: 0 0 0.35rem;
}

.bc-why {
  color: #646464;
  font-size: 0.9375rem;
  margin: 0 0 1rem;
}

.bc-sheet-foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: #fff;
  border-top: 1px solid #eee;

  ::v-deep .btn-primary {
    flex: 1 1 auto;
  }

  ::v-deep .btn-default {
    flex: 0 1 auto;
  }
}

body.--dark {
  .bc-sheet,
  .bc-sheet-head,
  .bc-sheet-foot {
    background: #373737;
  }

  .bc-sheet-foot {
    border-top-color: #555;
  }

  .bc-n,
  .bc-why {
    color: #c4c4c4;
  }

  .bc-q {
    color: #e6e6e6;
  }
}
</style>

<style>
body.--bc-sheet-open {
  overflow: hidden;
}
</style>

